import { useState } from "react";

import { api } from "@/lib/api";
import { relativeTime } from "@/lib/hooks";
import type { Equipment, EventItem, EventList } from "@/types";

/**
 * One tap to put the cover on, one to take it off.
 *
 * A solar cover goes on and off most days, and the only way to record that was
 * a nine-option event dialog with a datetime field and a UUID box. That is a
 * form you fill in once to try it and never again — and an event nobody logs is
 * worse than no event type at all, because the gaps look like the cover was
 * never used rather than like nobody wrote it down.
 *
 * The full dialog is still there and is still the right place for corrections,
 * backdating, and picking which cover. This is the path for the ordinary twice
 * a day case: phone in hand, cover in the other hand, standing at the pool.
 *
 * **It shows only when there is a cover to talk about.** A vessel with no cover
 * in its equipment log gets nothing — a hot tub does not want this control, and
 * neither does a pool that has never owned one.
 */
export default function SolarCoverToggle({
  covers,
  open,
  onChanged,
}: {
  /** Equipment in the `cover` category. Empty hides the control entirely. */
  covers: Equipment[];
  /** The open solar cover span, if one is running. */
  open: EventItem | null;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (covers.length === 0) return null;

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      if (open) {
        await api.post(`/api/events/${open.id}/close`, {
          ends_at: new Date().toISOString(),
        });
      } else {
        // Only auto-link when there is exactly one, and only when it is still
        // installed. Guessing between two covers would put a wrong answer in
        // the log and look deliberate doing it.
        const active = covers.filter((item) => item.is_active);
        await api.post("/api/events", {
          event_type: "solar_cover",
          starts_at: new Date().toISOString(),
          ends_at: null,
          payload: {
            event_type: "solar_cover",
            ...(active.length === 1 ? { equipment_id: active[0].id } : {}),
          },
          notes: null,
        });
      }
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not record that.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card flex items-center justify-between gap-3 p-4">
      <div className="min-w-0">
        <p className="text-sm font-medium">Solar cover</p>
        <p className="unit mt-0.5">
          {open ? `On since ${relativeTime(open.starts_at)}` : "Off"}
        </p>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className="btn btn-quiet shrink-0 px-3 py-2 text-sm"
      >
        {busy ? "Saving…" : open ? "Take it off" : "Put it on"}
      </button>
      {error && (
        <p role="alert" className="w-full text-xs text-(--color-status-high)">
          {error}
        </p>
      )}
    </div>
  );
}

/** The open solar cover span in a list of events, or null. */
export function openCover(
  events: EventList | null | undefined,
): EventItem | null {
  return (
    (events?.items ?? []).find(
      (item) => item.event_type === "solar_cover" && item.is_open,
    ) ?? null
  );
}
